import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const SectionTransition = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const navigate = useNavigate();
    const location = useLocation();
    const { currentSection, nextSection, sectionIndex, phaseIndex } = location.state || {};

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleContinue = () => {
        navigate('/questionnaire', { 
            replace: true,
            state: { 
                fromTransition: true,
                sectionIndex: sectionIndex,
                phaseIndex: phaseIndex
            }
        });
    };

    return (
        <div className="w-full min-h-screen bg-[#1C1D1F] relative overflow-hidden">
            {/* Main Content Container */}
            <div className="relative w-full min-h-screen flex items-center justify-center z-10">
                <div className={`max-w-[900px] w-full flex flex-col items-center ${isMobile ? 'gap-[24px] px-4 py-[60px]' : 'gap-[32px] px-8 py-[120px]'} text-center`}>
                    {/* Main Heading */}
                    <h1
                        className="font-archivo font-bold text-white"
                        style={{
                            fontSize: isMobile ? '28px' : '44px',
                            lineHeight: isMobile ? '36px' : '56px',
                            letterSpacing: '-0.02em'
                        }}
                    >
                        Data Foundations: Checked. Next Up: Agentic Readiness
                    </h1>

                    {/* Description and CTA Text grouped */}
                    <div className={`flex flex-col items-center ${isMobile ? 'gap-[20px]' : 'gap-[24px]'}`}>
                        {/* Subheading - Description */}
                        <p
                            className="font-inter font-normal max-w-[800px]"
                            style={{
                                fontSize: isMobile ? '16px' : '20px',
                                lineHeight: isMobile ? '24px' : '30px',
                                color: '#A7A9BE'
                            }}
                        >
                            This section explores how your organization uses Generative AI, automation, and intelligent agents to boost efficiency, enhance customer experience, and scale innovation responsibly. We shift focus from data systems to intelligent systems - measuring how seamlessly AI is woven into the way your company operates, grows, and leads.                    </p>

                        {/* CTA Text */}
                        <p
                            className="font-inter font-medium text-white max-w-[700px]"
                            style={{
                                fontSize: isMobile ? '16px' : '18px',
                                lineHeight: isMobile ? '24px' : '27px'
                            }}
                        >
                            Measure how intelligently your organization operates, grows, and governs with AI
                        </p>
                    </div>

                    {/* CTA Button */}
                    <button
                        onClick={handleContinue}
                        className="group hover:opacity-90 transition-opacity mt-4"
                        style={{
                            width: isMobile ? '140px' : '160px',
                            height: isMobile ? '44px' : '48px',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingTop: isMobile ? '10px' : '12px',
                            paddingRight: isMobile ? '24px' : '32px',
                            paddingBottom: isMobile ? '10px' : '12px',
                            paddingLeft: isMobile ? '24px' : '32px',
                            gap: '8px',
                            background: 'linear-gradient(91.9deg, #145F96 0.8%, #249FDA 99.45%)',
                            borderRadius: '8px',
                            border: 'none',
                            cursor: 'pointer',
                            boxShadow: '0px 4px 12px rgba(36, 159, 218, 0.3)'
                        }}
                    >
                        <span
                            style={{
                                fontFamily: 'Inter',
                                fontWeight: 500,
                                fontStyle: 'normal',
                                fontSize: isMobile ? '14px' : '16px',
                                lineHeight: '24px',
                                color: '#FFFFFF',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Let's start
                        </span>
                        <svg
                            width="20"
                            height="21"
                            viewBox="0 0 20 21"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="group-hover:translate-x-1 transition-transform"
                        >
                            <path
                                d="M5.83331 14.6668L14.1666 6.3335M14.1666 6.3335H5.83331M14.1666 6.3335V14.6668"
                                stroke="white"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Animated Light Effects Background */}
            <div
                className="absolute left-1/2 -translate-x-1/2 overflow-hidden"
                style={{
                    width: '100vw',
                    height: '100vh',
                    top: '0'
                }}
            >
                {/* Top fade overlay to blend with background */}
                <div
                    className="absolute inset-0 z-10 pointer-events-none"
                    style={{
                        background: 'linear-gradient(to bottom, #1C1D1F 0%, rgba(28, 29, 31, 0.8) 40%, rgba(28, 29, 31, 0.3) 70%, transparent 90%)'
                    }}
                />

                {/* Animated gradient container */}
                <div className="relative w-full h-full">
                    {/* Vector 1 - Large sweeping gradient base */}
                    <div
                        className="absolute animate-sweep-horizontal"
                        style={{
                            width: isMobile ? '600px' : '1011.52px',
                            height: isMobile ? '500px' : '809.13px',
                            left: isMobile ? '-300px' : '-484px',
                            top: isMobile ? '300px' : '450px',
                            background: 'linear-gradient(180deg, rgba(16, 114, 186, 0.6) 0%, rgba(16, 114, 186, 0.4) 100%)',
                            mixBlendMode: 'screen',
                            filter: 'blur(244.391px)',
                            transform: 'rotate(60deg)',
                            transformOrigin: 'center center',
                            opacity: '0.8'
                        }}
                    />

                    {/* Ellipse 3 - Sweeping blue wave */}
                    <div
                        className="absolute animate-sweep-horizontal"
                        style={{
                            width: isMobile ? '400px' : '710.7px',
                            height: isMobile ? '150px' : '265.7px',
                            left: isMobile ? '50px' : '151px',
                            top: isMobile ? '250px' : '400px',
                            background: 'rgba(7, 181, 255, 0.7)',
                            mixBlendMode: 'screen',
                            filter: 'blur(167.606px)',
                            borderRadius: '50%',
                            transform: 'rotate(45.74deg)',
                            transformOrigin: 'center center',
                            opacity: '0.7',
                            animationDelay: '1s'
                        }}
                    />

                    {/* Ellipse 2 - Sweeping cyan orb */}
                    <div
                        className="absolute animate-sweep-horizontal"
                        style={{
                            width: isMobile ? '300px' : '500.97px',
                            height: isMobile ? '300px' : '500.97px',
                            left: isMobile ? '150px' : '303px',
                            top: isMobile ? '350px' : '480px',
                            background: 'rgba(69, 197, 224, 0.6)',
                            mixBlendMode: 'screen',
                            filter: 'blur(167.606px)',
                            borderRadius: '50%',
                            transform: 'rotate(60deg)',
                            transformOrigin: 'center center',
                            opacity: '0.6',
                            animationDelay: '2s'
                        }}
                    />

                    {/* Horizontal moving gradient overlay - very subtle */}
                    <div
                        className="absolute animate-slide-horizontal"
                        style={{
                            width: '200%',
                            height: '100%',
                            left: '-50%',
                            background: 'linear-gradient(90deg, transparent 0%, rgba(36, 159, 218, 0.08) 25%, rgba(69, 197, 224, 0.1) 50%, rgba(7, 181, 255, 0.08) 75%, transparent 100%)',
                            mixBlendMode: 'screen'
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default SectionTransition;
